import { Edition, EditionLabels } from '../enums';
import {
  CreateUltraCheckoutSessionRequest,
  CreateUltraCheckoutSessionResponse,
} from '../types';

export const ULTRA_PLAN = 'ultra';

export type UltraBillingInterval = 'monthly' | 'yearly';

export function canUpgradeToUltra(edition?: Edition | null): boolean {
  return edition != null && edition !== Edition.Team;
}

export function canUpgradeToUltraByLabel(editionName?: string): boolean {
  return Boolean(editionName) && editionName !== EditionLabels.Team;
}

export const ULTRA_UPGRADE_LABEL = 'Upgrade to Superconnector Ultra';
export const ULTRA_UNLOCK_LABEL = 'Unlock Superconnector Ultra';

export type UltraCheckoutIntent = {
  plan: typeof ULTRA_PLAN;
  coupon?: string;
  interval?: UltraBillingInterval;
};

type QueryValue = string | string[] | undefined;

export function firstQueryValue(value: QueryValue): string {
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
}

function setIfPresent(
  params: URLSearchParams,
  key: string,
  value?: string
): void {
  if (value) params.set(key, value);
}

export function parseUltraBillingInterval(
  value?: QueryValue
): UltraBillingInterval | undefined {
  const interval = firstQueryValue(value).trim().toLowerCase();
  if (interval === 'monthly') return 'monthly';
  if (interval === 'yearly' || interval === 'annual') return 'yearly';
  return undefined;
}

/** Ultra intent comes only from the URL — never sessionStorage. */
export function readUltraCheckoutIntent(query?: {
  plan?: QueryValue;
  coupon?: QueryValue;
  interval?: QueryValue;
}): UltraCheckoutIntent | null {
  const coupon = firstQueryValue(query?.coupon).trim() || undefined;
  const interval = parseUltraBillingInterval(query?.interval);
  const isUltra =
    Boolean(coupon) ||
    Boolean(interval) ||
    firstQueryValue(query?.plan) === ULTRA_PLAN;

  if (!isUltra) return null;

  return {
    plan: ULTRA_PLAN,
    ...(coupon ? { coupon } : {}),
    ...(interval ? { interval } : {}),
  };
}

export function getUltraCheckoutPath(
  coupon?: string,
  interval?: UltraBillingInterval
): string {
  const params = new URLSearchParams();
  setIfPresent(params, 'coupon', coupon);
  setIfPresent(params, 'interval', interval);
  const query = params.toString();
  return query ? `/buy/ultra?${query}` : '/buy/ultra';
}

export function getUltraSignupPath(
  coupon?: string,
  interval?: UltraBillingInterval
): string {
  const params = new URLSearchParams({ plan: ULTRA_PLAN });
  setIfPresent(params, 'coupon', coupon);
  setIfPresent(params, 'interval', interval);
  return `/signup/completeSignup?${params.toString()}`;
}

export function getPaymentRetryPath(query?: {
  from?: QueryValue;
  coupon?: QueryValue;
  interval?: QueryValue;
}): string {
  if (firstQueryValue(query?.from).trim().toLowerCase() !== ULTRA_PLAN) {
    return '/pricing';
  }

  return getUltraCheckoutPath(
    firstQueryValue(query?.coupon).trim() || undefined,
    parseUltraBillingInterval(query?.interval)
  );
}

export function buildUltraSignupLoginUrl(
  intent: Pick<UltraCheckoutIntent, 'coupon' | 'interval'> & { email?: string }
): string {
  const params = new URLSearchParams({
    logintype: 'create',
    'redirect-url': '/signup/completeSignup',
    plan: ULTRA_PLAN,
  });
  setIfPresent(params, 'login_hint', intent.email);
  setIfPresent(params, 'coupon', intent.coupon);
  setIfPresent(params, 'interval', intent.interval);
  return `/api/auth/login?${params.toString()}`;
}

function buildUltraCheckoutUrls(
  coupon?: string,
  interval?: UltraBillingInterval
): { successUrl: string; cancelUrl: string } {
  const origin = window.location.origin;
  const extras = [
    `from=${ULTRA_PLAN}`,
    coupon ? `coupon=${encodeURIComponent(coupon)}` : '',
    interval ? `interval=${interval}` : '',
  ]
    .filter(Boolean)
    .join('&');

  return {
    successUrl: `${origin}/payment/status?session_id={CHECKOUT_SESSION_ID}&${extras}`,
    cancelUrl: `${origin}${getUltraCheckoutPath(coupon, interval)}`,
  };
}

function readErrorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) {
    try {
      return readErrorMessage(JSON.parse(payload), payload);
    } catch {
      return payload;
    }
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const nested =
      record.error ??
      record.Message ??
      record.message ??
      record.ExceptionMessage;
    if (typeof nested === 'string' && nested.trim()) {
      return readErrorMessage(nested, nested);
    }
  }

  return fallback;
}

export async function startUltraCheckout(options: {
  interval: UltraBillingInterval;
  coupon?: string;
  seats?: number;
}): Promise<void> {
  if (typeof window === 'undefined') return;

  const { successUrl, cancelUrl } = buildUltraCheckoutUrls(
    options.coupon,
    options.interval
  );
  const seats = Math.min(100, Math.max(1, Math.floor(options.seats || 1)));
  const body: CreateUltraCheckoutSessionRequest = {
    interval: options.interval,
    successUrl,
    cancelUrl,
    seats,
    ...(options.coupon ? { coupon: options.coupon } : {}),
  };

  const res = await fetch('/api/payment/createCheckoutSession', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const raw = await res.text();
  let payload: unknown = raw;
  try {
    payload = raw ? JSON.parse(raw) : raw;
  } catch {
    payload = raw;
  }

  if (!res.ok) {
    throw new Error(
      readErrorMessage(
        payload,
        'Unable to start Ultra checkout. Please try again.'
      )
    );
  }

  const data = payload as CreateUltraCheckoutSessionResponse;
  if (!data?.url) {
    throw new Error('Checkout URL was not returned. Please try again.');
  }

  window.location.href = data.url;
}
